import {
  getPracticeStatus,
  getPracticeSession,
  startNextPracticeQuestion,
  getPracticePresignedUrl,
  uploadPracticeImage,
  submitPracticeAttempt,
  getPracticePoints
} from "services/api/practice/practiceService";
import type { PracticeSessionData } from "services/api/practice/practiceService.types";

// Only initial entry can start a session. Recovery reads the server's current state.
export const loadPracticeState = async (
  token: string,
  startIfNeeded = false
) => {
  const {
    data: { status }
  } = await getPracticeStatus(token);
  if (
    status === "not_available" ||
    status === "finished" ||
    (status === "not_started" && !startIfNeeded)
  ) {
    return null;
  }
  if (status === "not_started") {
    try {
      await startNextPracticeQuestion(token);
    } catch (error) {
      const errorDetails = error as {
        message?: unknown;
        text?: unknown;
        json?: unknown;
      };
      const errorText = [
        errorDetails.message,
        errorDetails.text,
        typeof errorDetails.json === "string"
          ? errorDetails.json
          : JSON.stringify(errorDetails.json ?? "")
      ]
        .filter((value): value is string => typeof value === "string")
        .join(" ")
        .toLowerCase();

      if (!errorText.includes("current question not completed")) {
        throw error;
      }
    }
  }
  const { data: session } = await getPracticeSession(token);
  return session;
};

export const submitImageAttempt = async (
  token: string,
  session: PracticeSessionData,
  image: string
) => {
  const mimeType = image.match(/^data:(image\/[a-zA-Z0-9.+-]+);base64,/)?.[1];
  if (mimeType !== "image/jpeg" && mimeType !== "image/png") {
    throw new Error("Unsupported practice image format");
  }
  const { data: upload } = await getPracticePresignedUrl(token, {
    file_type: "answer",
    count: 1,
    format: mimeType.replace("image/", "")
  });
  const url = upload.presigned_urls[0];
  const key = upload.image_keys[0];
  if (!url || !key) throw new Error("Practice upload data is incomplete");
  await uploadPracticeImage(url, image, mimeType);
  const { data: result } = await submitPracticeAttempt(token, {
    practice_session_id: session.id,
    question_id: session.current_question.id,
    attempt_type: "ai",
    platform: "web",
    ai_image_key: key,
    mcq_selected_answer_id: null
  });
  const { data: updatedSession } = await getPracticeSession(token);
  return { result, session: updatedSession };
};

export const submitMcqAttempt = async (
  token: string,
  session: PracticeSessionData,
  answerId: string
) => {
  const { data: result } = await submitPracticeAttempt(token, {
    practice_session_id: session.id,
    question_id: session.current_question.id,
    attempt_type: "mcq",
    platform: "web",
    ai_image_key: null,
    mcq_selected_answer_id: answerId
  });
  const { data: updatedSession } = await getPracticeSession(token);
  return { result, session: updatedSession };
};

export const advancePracticeQuestion = async (token: string) => {
  await startNextPracticeQuestion(token);
  return (await getPracticeSession(token)).data;
};

export const completePracticeSession = async (token: string) => {
  await startNextPracticeQuestion(token);
};

export const loadPracticePoints = async (token: string, sessionId: string) =>
  (await getPracticePoints(token, sessionId)).data;
